import React, { useEffect, useMemo, useState } from "react";
import { V2_ROUTES } from "../state/useV2Navigation";
import { useV2Theme } from "../theme/ThemeProvider";
import { v2cx } from "../theme/tokens";
import { useV2EnrichmentReview } from "../state/V2EnrichmentReviewContext";
import {
  NAV_ITEMS,
  MOBILE_PRIMARY_KEYS,
  MOBILE_MORE_KEYS,
  IconForRoute,
  inboxBellTitle,
  approvalsBellTitle,
  formatNavBadgeCount,
} from "../nav/navItems";

/**
 * iOS/Android-style bottom tabs + "More" sheet for secondary destinations.
 * Desktop (≥ md) is handled by the left icon rail.
 */
export default function V2MobileBottomNav({
  route,
  routes,
  onNavigate,
  onSignOut,
  isNavAllowed,
}) {
  const { tokens } = useV2Theme();
  const [moreOpen, setMoreOpen] = useState(false);
  const allowed =
    typeof isNavAllowed === "function" ? isNavAllowed : () => true;
  const {
    pendingReviewCount,
    inboxNotificationCount,
    hasInboxNotifications,
    baselineApprovalNotificationCount,
    hasBaselineApprovalNotifications,
  } = useV2EnrichmentReview();

  const available = useMemo(
    () => new Set(Array.isArray(routes) ? routes : []),
    [routes]
  );

  const primaryItems = useMemo(
    () =>
      MOBILE_PRIMARY_KEYS.map((key) => NAV_ITEMS.find((i) => i.key === key)).filter(
        (item) => item && available.has(item.key)
      ),
    [available]
  );

  const moreItems = useMemo(
    () =>
      MOBILE_MORE_KEYS.map((key) => NAV_ITEMS.find((i) => i.key === key)).filter(
        (item) => item && available.has(item.key) && !item.desktopOnly
      ),
    [available]
  );

  const moreActive = moreItems.some((item) => item.key === route);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMoreOpen(false);
    };
    window.addEventListener("keydown", onKey);
    // Do not lock body overflow — fights the shell height lock on mobile.
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [moreOpen]);

  const badgeFor = (key) => {
    if (key === V2_ROUTES.insightsInbox && hasInboxNotifications) {
      return {
        count: inboxNotificationCount,
        title: inboxBellTitle({ pendingReviewCount, inboxNotificationCount }),
      };
    }
    if (key === V2_ROUTES.baselineApprovals && hasBaselineApprovalNotifications) {
      return {
        count: baselineApprovalNotificationCount,
        title: approvalsBellTitle(baselineApprovalNotificationCount),
      };
    }
    return null;
  };

  const go = (key) => {
    setMoreOpen(false);
    onNavigate(key);
  };

  const lockTitle = (item) =>
    allowed(item.key)
      ? item.title || item.label
      : "Guided demo — finish this step before opening other areas";

  return (
    <>
      <nav
        className={tokens.mobileNav.root}
        aria-label="Primary"
      >
        <div className={tokens.mobileNav.inner}>
          {primaryItems.map((item) => {
            const active = route === item.key;
            const locked = !allowed(item.key);
            const badge = badgeFor(item.key);
            const label = item.shortLabel || item.label;
            return (
              <button
                key={item.key}
                type="button"
                title={locked ? lockTitle(item) : badge?.title || item.title || item.label}
                aria-label={
                  locked
                    ? `${item.label} (locked during guided demo)`
                    : badge
                      ? `${item.label}, ${badge.count} pending`
                      : item.label
                }
                aria-disabled={locked || undefined}
                aria-current={active ? "page" : undefined}
                onClick={() => go(item.key)}
                className={v2cx(
                  tokens.mobileNav.item,
                  active ? tokens.mobileNav.itemActive : tokens.mobileNav.itemIdle,
                  locked && !active && "cursor-not-allowed opacity-40"
                )}
              >
                <span className="relative inline-flex">
                  <IconForRoute
                    routeKey={item.key}
                    active={active}
                    tokens={tokens}
                    className="w-[22px] h-[22px]"
                  />
                  {badge ? (
                    <span
                      className="absolute -top-1.5 -right-2.5 flex min-h-[1.05rem] min-w-[1.05rem] items-center justify-center rounded-full bg-amber-500 px-1 text-[9px] font-bold leading-none text-white tabular-nums"
                      aria-hidden
                    >
                      {formatNavBadgeCount(badge.count)}
                    </span>
                  ) : null}
                </span>
                <span
                  className={
                    active
                      ? tokens.mobileNav.labelActive
                      : tokens.mobileNav.labelIdle
                  }
                >
                  {label}
                </span>
              </button>
            );
          })}

          <button
            type="button"
            aria-label="More"
            aria-expanded={moreOpen}
            aria-haspopup="dialog"
            onClick={() => setMoreOpen(true)}
            className={v2cx(
              tokens.mobileNav.item,
              moreActive || moreOpen
                ? tokens.mobileNav.itemActive
                : tokens.mobileNav.itemIdle
            )}
          >
            <IconForRoute
              routeKey="more"
              active={moreActive || moreOpen}
              tokens={tokens}
              className="w-[22px] h-[22px]"
            />
            <span
              className={
                moreActive || moreOpen
                  ? tokens.mobileNav.labelActive
                  : tokens.mobileNav.labelIdle
              }
            >
              More
            </span>
          </button>
        </div>
      </nav>

      {moreOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden" role="presentation">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
            aria-label="Close menu"
            onClick={() => setMoreOpen(false)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="More destinations"
            className={tokens.mobileNav.sheet}
          >
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-400/50" aria-hidden />
            <p className="px-1 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              More
            </p>
            <ul className="space-y-1">
              {moreItems.map((item) => {
                const active = route === item.key;
                const locked = !allowed(item.key);
                return (
                  <li key={item.key}>
                    <button
                      type="button"
                      title={lockTitle(item)}
                      aria-disabled={locked || undefined}
                      onClick={() => go(item.key)}
                      className={v2cx(
                        tokens.mobileNav.sheetItem,
                        active && tokens.mobileNav.sheetItemActive,
                        locked && !active && "cursor-not-allowed opacity-40"
                      )}
                    >
                      <IconForRoute
                        routeKey={item.key}
                        active={active}
                        tokens={tokens}
                      />
                      <span className="font-medium">{item.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {typeof onSignOut === "function" ? (
              <button
                type="button"
                onClick={() => {
                  setMoreOpen(false);
                  onSignOut();
                }}
                className={v2cx(tokens.mobileNav.sheetItem, "mt-3 text-rose-700")}
              >
                Sign out
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
